import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryParam, StringParam } from 'use-query-params';
import { useTranslation } from 'react-i18next';
import Select from '../select';
import { FilterContainer, FilterWrapper, FilterGroup } from './styles';
import { billingService, clientService, configurationService } from '../../api';
import Input from '../input';
import DatePickerCustom from '../date-picker';
import { DateValue, parseDate } from '@internationalized/date';

interface FilterOption {
  display: string;
  value: string;
}

type FilterEntry = FilterOption[] | {
  options?: FilterOption[];
  type?: 'date' | 'number' | 'text';
};

type FilterOptions = Record<string, FilterEntry | undefined>;

interface FilterBarProps {
  filterOptions?: FilterOptions;
}

const SEARCHABLE_FILTER_KEYS = new Set([
  'user',
  'user_id',
  'client',
  'client_credit__client',
  'invoice__client',
  'project__service__client',
  'project__service__client__id',
  'user_groups',
  'product',
  'configuration',
  'groups'
]);
const REMOTE_FILTER_KEYS = new Set([
  'user',
  'user_id',
  'user_groups',
  'client',
  'client_credit__client',
  'invoice__client',
  'project__service__client',
  'project__service__client__id',
  'product',
  'configuration',
  'groups'
]);

const FilterBar: FC<FilterBarProps> = ({ filterOptions }) => {
  const [t] = useTranslation();
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const [remoteOptions, setRemoteOptions] = useState<Record<string, FilterOption[]>>({});
  const [searchLoading, setSearchLoading] = useState<Record<string, boolean>>({});
  // Store selected option labels to display them even when not in current options list
  const [selectedLabels, setSelectedLabels] = useState<Record<string, string>>({});
  
  // Parse current filters from URL
  const parseFiltering = (filterString: string | null | undefined): Record<string, string> => {
    if (!filterString) return {};
    
    const filters: Record<string, string> = {};
    const parts = filterString.split(',');
    
    parts.forEach(part => {
      const [key, value] = part.split(':');
      if (key && value) {
        filters[key.trim()] = value.trim();
      }
    });
    
    return filters;
  };
  
  // Build filter string from current selections
  const buildFiltering = (filters: Record<string, string>): string => {
    return Object.entries(filters)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => `${key}:${value}`)
      .join(',');
  };
  
  const currentFilters = parseFiltering(filtering);
  
  const handleFilterChange = (filterKey: string, value: any, label?: string) => {
    const extracted = (value && typeof value === 'object' && 'value' in value) ? (value as any).value : value;
    const extractedLabel = (value && typeof value === 'object' && 'label' in value) ? (value as any).label : label;
    const newFilters = { ...currentFilters };

    if (extracted === '' || extracted === null) {
      delete newFilters[filterKey];
      // Clear stored label
      setSelectedLabels(prev => {
        const updated = { ...prev };
        delete updated[filterKey];
        return updated;
      });
    } else {
      newFilters[filterKey] = extracted;
      // Store label for display
      if (extractedLabel && REMOTE_FILTER_KEYS.has(filterKey)) {
        setSelectedLabels(prev => ({ ...prev, [filterKey]: extractedLabel }));
      }
    }

    const newFilterString = buildFiltering(newFilters);
    setFiltering(newFilterString || undefined);
  };
  
  const fetchRemoteOptions = useCallback(async (filterKey: string, term: string) => {
    if (!REMOTE_FILTER_KEYS.has(filterKey)) return;
    setSearchLoading((prev) => ({ ...prev, [filterKey]: true }));
    try {
      if (filterKey === 'user' || filterKey === 'user_id') {
        const res = await clientService.getUsers({
          page: 1,
          pageSize: 10,
          search: term || '',
          is_superuser: false,
        });
        const users = res?.data?.objects || [];
        const options = users.map((user: any) => ({
          label: user?.display || user?.full_name || user?.email || `${user?.id}`,
          value: user?.id,
          id: user?.id,
        }));
        setRemoteOptions((prev) => ({ ...prev, [filterKey]: options }));
      }
      if (filterKey === 'user_groups') {
        const res = await clientService.getUserGroups({
          page: 1,
          pageSize: 10,
          ordering: 'name',
          search: term || '',
        });
        const groups = res?.data?.objects || [];
        const options = groups.map((group: any) => ({
          label: group?.name || `${group?.id}`,
          value: group?.id,
          id: group?.id,
        }));
        setRemoteOptions((prev) => ({ ...prev, [filterKey]: options }));
      }
      if (filterKey === 'client' || filterKey === 'client_credit__client' || filterKey === 'invoice__client' || filterKey === 'project__service__client' || filterKey === 'project__service__client__id') {
        const res = await clientService.getClientList({
          page: 1,
          pageSize: 10,
          search: term || '',
        });
        const clients = res?.data?.objects || [];
        const options = clients.map((client: any) => ({
          label: client?.display || client?.name || `${client?.id}`,
          value: client?.id,
          id: client?.id,
        }));
        setRemoteOptions((prev) => ({ ...prev, [filterKey]: options }));
      }
      if (filterKey === 'product') {
        const res = await billingService.getProducts({
          page: 1,
          page_size: 10,
          search: term || '',
        });
        const products = res?.data?.objects || [];
        const options = products.map((product: any) => ({
          label: product?.name || `${product?.id}`,
          value: product?.id,
          id: product?.id,
        }));
        setRemoteOptions((prev) => ({ ...prev, [filterKey]: options }));
      }
      if (filterKey === 'configuration') {
        const res = await configurationService.getConfiguratonsList({
          page: 1,
          page_size: 20,
          search: term || '',
        });
        const configurations = res?.data?.objects || [];
        const options = configurations.map((config: any) => ({
          label: config?.name || `${config?.id}`,
          value: config?.id,
          id: config?.id,
        }));
        setRemoteOptions((prev) => ({ ...prev, [filterKey]: options }));
      }
      if (filterKey === 'groups') {
        const res = await clientService.getClientGroups({
          page: 1,
          pageSize: 20,
          ordering: 'name',
          search: term || '',
        });
        const groups = res?.data?.objects || [];
        const options = groups.map((group: any) => ({
          label: group?.name || `${group?.id}`,
          value: group?.id,
          id: group?.id,
        }));
        setRemoteOptions((prev) => ({ ...prev, [filterKey]: options }));
      }
    } catch (err) {
      // noop on fetch error
    } finally {
      setSearchLoading((prev) => ({ ...prev, [filterKey]: false }));
    }
  }, []);

  // Load initial options for remote filter keys on mount
  useEffect(() => {
    if (!filterOptions) return;

    const remoteKeysToLoad = Object.keys(filterOptions).filter(key => REMOTE_FILTER_KEYS.has(key));
    remoteKeysToLoad.forEach(key => {
      // Only load if we don't already have options
      if (!remoteOptions[key] || remoteOptions[key].length === 0) {
        fetchRemoteOptions(key, '');
      }
    });
  }, [filterOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update selectedLabels when remoteOptions load and contain selected values
  useEffect(() => {
    const newLabels: Record<string, string> = {};
    Object.entries(currentFilters).forEach(([key, value]) => {
      if (REMOTE_FILTER_KEYS.has(key) && value && remoteOptions[key]) {
        const foundOption = remoteOptions[key].find(
          opt => String(opt.value) === String(value)
        );
        if (foundOption && !selectedLabels[key]) {
          newLabels[key] = foundOption.label || foundOption.display || `ID: ${value}`;
        }
      }
    });
    if (Object.keys(newLabels).length > 0) {
      setSelectedLabels(prev => ({ ...prev, ...newLabels }));
    }
  }, [remoteOptions, currentFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!filterOptions) return null;
  
  const filterLabels: Record<string, string> = {
  status: 'STATUS',
  user: 'USER',
  user_id: 'USER',
  source: 'SOURCE',
  destination: 'DESTINATION',
  client_credit__client: 'CREDIT_RELATED_CLIENT',
  invoice__client: 'INVOICE_RELATED_CLIENT',
  issue_date: 'ISSUE_DATE',
  due_date: 'DUE_DATE',
  order_date: 'ORDER_DATE',
  total: 'TOTAL',
  client: 'CLIENT',
  date_added: 'DATE',
  destination_amount: 'DESTINATION_AMOUNT',
  source_amount: 'SOURCE_AMOUNT',
  created_at: 'CREATED_AT',
  updated_at: 'UPDATED_AT',
  activated_at: 'ACTIVATED_AT',
  suspended_at: 'SUSPENDED_AT',
  terminated_at: 'TERMINATED_AT',
  paid_until: 'PAID_UNTIL',
  product: 'PRODUCT',
  country: 'COUNTRY',
  'services__product__product_type': 'PRODUCT_TYPE',
  services__product: 'PRODUCT',
  created: 'CREATED_AT',
  date_created: 'CREATED_AT',
  configuration: 'CONFIGURATION',
  groups: 'CLIENT_GROUP',
  services__openstack_project__instance__region: 'REGION',
  services__openstack_project__instance__flavor: 'FLAVOR',
  services__openstack_project__instance__host_name: 'HYPERVISOR',
  tax_exempt: 'TAX_EXEMPT',
  has_billing_agreement: 'HAS_BILLING_AGREEMENT',
  has_services: 'HAS_SERVICES',
  has_instances: 'HAS_INSTANCES',
  has_reseller_services: 'IS_RESELLER',
  assigned_to_reseller: 'ASSIGNED_TO_RESELLER',
  project__service__client__id: 'CLIENT',
  project__service__client: 'CLIENT',
  task_state: 'TASK_STATE',
  flavor: 'FLAVOR',
  flavor__name: 'FLAVOR_NAME',
  availability_zone: 'AVAILABILITY_ZONE',
  availability_zones__contains: 'AVAILABILITY_ZONE',
  host_name: 'HYPERVISOR',
  image: 'IMAGE',
  image__os_distro: 'OS_DISTRO',
  region: 'REGION',
  network__region: 'REGION',
  project__service__client__status: 'CLIENT_STATUS',
  project__service__status: 'SERVICE_STATUS',
  user_groups: 'USER_GROUPS',
  is_active: 'IS_ACTIVE',
  is_reseller: 'IS_RESELLER',
  is_staff: 'IS_STAFF',
  is_superuser: 'IS_SUPERUSER',
  date_joined: 'DATE_JOINED',
  last_login: 'LAST_LOGIN',
  owner: 'OWNER',
  parent: 'PARENT',
  public: 'PUBLIC',
  default: 'DEFAULT',
  // Cloud resource filters
  hypervisor_type: 'HYPERVISOR_TYPE',
  protected: 'PROTECTED',
  min_disk: 'MIN_DISK_SIZE',
  visibility: 'VISIBILITY',
  is_default: 'IS_DEFAULT',
  shared: 'SHARED',
  admin_state_up: 'ADMIN_STATE_UP',
  distributed: 'DISTRIBUTED',
  enable_snat: 'ENABLE_SNAT',
  network: 'NETWORK',
  port_security_enabled: 'PORT_SECURITY',
  memory_mb: 'MEMORY_MB',
  root_gb: 'ROOT_GB',
  vcpus: 'VCPUS',
  is_public: 'IS_PUBLIC',
  flavor_group: 'FLAVOR_GROUP',
  size: 'SIZE',
};

  const parseDateValue = (value?: string | null): DateValue | null => {
    if (!value) return null;
    try {
      // If it's a timestamp, convert to date string first
      if (/^\d+$/.test(value)) {
        const date = new Date(parseInt(value, 10));
        const isoString = date.toISOString().split('T')[0];
        return parseDate(isoString);
      }
      return parseDate(value);
    } catch {
      return null;
    }
  };

  // Convert date to timestamp for API (start of day for gte filter)
  const dateToTimestamp = (dateValue: DateValue): string => {
    const date = new Date(dateValue.year, dateValue.month - 1, dateValue.day, 0, 0, 0, 0);
    return date.getTime().toString();
  };

  // Get the actual filter key for date (check if gte version exists in URL)
  const getDateFilterKey = (baseKey: string): string => {
    return `${baseKey}__gte__date`;
  };

  // Parse date filter key from URL (remove __gte__date suffix)
  const parseDateFilterKey = (urlKey: string): string => {
    return urlKey.replace(/__gte__date$/, '').replace(/__lte__date$/, '');
  };
  
  return (
    <FilterContainer>
      {Object.entries(filterOptions).map(([filterKey, rawEntry]) => {
        const entry = Array.isArray(rawEntry) ? { options: rawEntry } : (rawEntry || {});
        const options = entry.options || [];
        const type = entry.type;
        const mergedOptions = remoteOptions[filterKey] && remoteOptions[filterKey].length > 0
          ? remoteOptions[filterKey]
          : options;

        // Build data array with "All" option first
        const mappedOptions = mergedOptions.map(opt => ({
          label: opt.display ?? opt.label,
          value: opt.value,
          id: opt.id ?? opt.value,
        }));

        // If there's a selected value not in the list, add it with stored or fallback label
        const selectedValue = currentFilters[filterKey];
        const hasSelectedInList = !selectedValue || mappedOptions.some(opt => String(opt.value) === String(selectedValue));

        if (selectedValue && !hasSelectedInList) {
          const storedLabel = selectedLabels[filterKey] || `ID: ${selectedValue}`;
          mappedOptions.unshift({
            label: storedLabel,
            value: selectedValue,
            id: selectedValue,
          });
        }

        const data = [
          { label: t('ALL'), value: '', id: '' },
          ...mappedOptions,
        ];

        if (type === 'date') {
          // Date filters use __gte__date suffix in URL
          const dateFilterKey = getDateFilterKey(filterKey);
          const dateValue = parseDateValue(currentFilters[dateFilterKey]);
          return (
            <FilterWrapper key={filterKey}>
              <DatePickerCustom
                title={t(filterLabels[filterKey] || filterKey)}
                value={dateValue || undefined}
                isClearable
                onChange={(val) => {
                  if (val) {
                    // Send as timestamp with __gte__date suffix
                    handleFilterChange(dateFilterKey, dateToTimestamp(val));
                  } else {
                    handleFilterChange(dateFilterKey, '');
                  }
                }}
              />
            </FilterWrapper>
          );
        }

        if (type === 'number' || type === 'text') {
          const inputType = type === 'number' ? 'number' : 'text';
          return (
            <FilterWrapper key={filterKey}>
              <Input
                title={t(filterLabels[filterKey] || filterKey)}
                placeholder={t(filterLabels[filterKey] || filterKey)}
                type={inputType}
                value={currentFilters[filterKey] || ''}
                handleChange={(val:any) => handleFilterChange(filterKey, val?.target ? val.target.value : val)}
              />
            </FilterWrapper>
          );
        }

        return (
          <FilterWrapper key={filterKey}>
            <Select
              title={t(filterLabels[filterKey] || filterKey)}
              data={data}
              selectedValue={currentFilters[filterKey] || ''}
              onChange={(value) => {
                // Find the label for the selected value
                const selectedOption = data.find(opt => String(opt.value) === String(value));
                const label = selectedOption?.label || '';
                handleFilterChange(filterKey, value, label);
              }}
              placeholder={`Select ${filterLabels[filterKey] || filterKey}`}
              hasSearch={SEARCHABLE_FILTER_KEYS.has(filterKey)}
              onSearchChange={REMOTE_FILTER_KEYS.has(filterKey) ? (value) => fetchRemoteOptions(filterKey, value) : undefined}
            />
          </FilterWrapper>
        );
      })}
    </FilterContainer>
  );
};

export default FilterBar;
